import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Table2, AlertTriangle, CheckCircle2 } from "lucide-react";

const inputCls =
  "rounded-none border-[#0A1F44]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono";

export const ImportDialog = ({ open, onOpenChange, onImported, year }) => {
  const [url, setUrl] = useState("");
  const [replace, setReplace] = useState(true);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const reset = () => { setPreview(null); setError(""); };

  useEffect(() => { setPreview(null); setError(""); }, [url]);

  const doPreview = async () => {
    if (!url.trim()) { toast.error("Paste your Google Sheets link first"); return; }
    setLoading(true); reset();
    try {
      const { data } = await api.post("/import/preview", { url });
      setPreview(data);
      if (data.count === 0) setError("No staff rows were found in that sheet.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not read that sheet.");
    } finally { setLoading(false); }
  };

  const doImport = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/import/commit", { url, replace }, { params: { year } });
      toast.success(`Imported ${data.imported} staff member${data.imported === 1 ? "" : "s"}`);
      onImported();
      onOpenChange(false);
      setUrl(""); reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setUrl(""); reset(); } }}>
      <DialogContent data-testid="import-dialog" className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0A1F44] flex items-center gap-2">
            <Table2 className="w-5 h-5 text-[#C0982E]" strokeWidth={1.5} /> Import from Google Sheets
          </DialogTitle>
          <DialogDescription className="text-[#0A1F44]/60">
            Paste a public sheet link (Share → "Anyone with the link"). Use a header row like
            Name, Email, Phone, Department, Room, Birthday — one person per row.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider">Google Sheets Link</Label>
            <Input
              data-testid="import-url-input"
              className={inputCls}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>

          {error && (
            <div data-testid="import-error" className="flex gap-2 border border-[#b3402a]/40 bg-[#b3402a]/5 p-3 text-sm text-[#b3402a]">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          {preview && preview.count > 0 && (
            <div data-testid="import-preview" className="border border-[#0A1F44]/20">
              <div className="flex items-center gap-2 bg-[#E6EAF1] px-4 py-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-[#0A1F44]" />
                Found <strong>{preview.count}</strong> staff members
              </div>
              <div className="max-h-52 overflow-y-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[#0A1F44]/50">
                      <th className="p-2 border-b border-[#0A1F44]/15">Name</th>
                      <th className="p-2 border-b border-[#0A1F44]/15">Dept</th>
                      <th className="p-2 border-b border-[#0A1F44]/15">Room</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((s, i) => (
                      <tr key={i} className="border-b border-[#0A1F44]/10">
                        <td className="p-2 font-serif">{s.first_name} {s.last_name}</td>
                        <td className="p-2">{s.department || "—"}</td>
                        <td className="p-2">{s.room || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-center gap-2 px-4 py-3 border-t border-[#0A1F44]/15 cursor-pointer">
                <Checkbox data-testid="import-replace-check" checked={replace} onCheckedChange={(v) => setReplace(!!v)} className="rounded-none border-[#0A1F44]" />
                <span className="text-sm">Replace the current directory (uncheck to add to it)</span>
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="import-cancel" variant="outline" className="rounded-none border-[#0A1F44] text-[#0A1F44] hover:bg-[#E6EAF1]" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!preview || preview.count === 0 ? (
            <Button data-testid="import-preview-btn" disabled={loading} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]" onClick={doPreview}>
              {loading ? "Reading…" : "Preview"}
            </Button>
          ) : (
            <Button data-testid="import-confirm-btn" disabled={loading} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E]" onClick={doImport}>
              {loading ? "Importing…" : `Import ${preview.count}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
